import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { DashboardProject } from '../../../types/project';
import { PipelineSetup, PipelineSetupRef } from './components/PipelineSetup';
import { FabricationSetup, FabricationSetupRef } from './components/FabricationSetup';
import { OthersSetup, OthersSetupRef } from './components/OthersSetup';
import { Loader2, Plus, Cable, Wrench, Activity, Save, Trash2 } from 'lucide-react';
import { pipeSizes } from '../../../mocks/material';
import { Button } from '../../../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import {
  ProjectSetupData,
  PipelineSetupData,
  FabricationSetupData,
  SetupState1,
  PipeSize1,
  buildSpoolDetailsPayload,
  getNumberOfSpoolsFromPipe,
  getSpoolDetailsFromPipe,
  normalizeSpoolDetailsRows,
} from '../../../types/setup';

import { SaveProjectSetup } from '../../../../../redux/Project/setUpSaga';
import { fetchProjectList } from '../../../../../redux/Project/action';
import { getAllPipeDetails, getListManufacturingCategory } from '../../../../../redux/Project/setupData/action';
import { useDispatch, useSelector } from 'react-redux';
import { assignJobNumberToPipe } from '../../../../../redux/WeldData/actions';
import { PipeInPipeSetup } from './components/PipeInPipeSetup';
import { getUnitFromSettings, convertSetupPipeToInchesForSave, convertSetupPipeFromInchesForDisplay } from '../../../../../utils/unitUtils';

interface ProjectSetupProps {
  project: DashboardProject;
  OnChanged: (hasUnsavedChanges: boolean) => void;
  canEdit?: boolean;
}

export const ProjectSetup: React.FC<ProjectSetupProps> = ({ project, OnChanged, canEdit = true }) => {
  const dispatch = useDispatch();
  const { pipeDetails, isPipeDetailsLoading, manufacturingCategories, isManufacturingCategoryLoading } = useSelector((state: any) => state.setupData || {});
  const [isLoading, setIsLoading] = useState(false);
  const [setupTab, setSetupTab] = useState<'pipeline' | 'fabrication' | 'others'>(project.project.projectTypes.pipeline ? 'pipeline' : project.project.projectTypes.fabrication ? 'fabrication' : 'others');
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [manufacturingList, setManufacturingList] = useState<any[]>([])
  const [selectedManufacturingId, setSelectedManufacturingId] = useState('')
  const [pipelineData, setPipelineData] = useState<any[]>([])
  const [unit, setUnit] = useState(project.project.unit || 'in')
  const [showValidation, setShowValidation] = useState(false);
  const [setupData, setSetupData] = useState<any>({});
  const [originalSetupData, setOriginalSetupData] = useState<any>({});
  const [editMode, setEditMode] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastProjectId, setLastProjectId] = useState<string | number | null>(null);

  const [defaultUnit, setDefaultUnit] = useState('in');
useEffect(() => {
    if (project.project.unit) {
      setDefaultUnit(project.project.unit || 'in');
      setUnit(project.project.unit);
    }
  }, [project.project.unit]);

  // Load initial data - only fetch if project ID changed or pipeDetails doesn't match current project
  useEffect(() => {
    if (isPipeDetailsLoading) {
      return;
    }
    const currentProjectId = project.project.projectId;

    // Check if project ID changed
    const projectIdChanged = lastProjectId !== null && lastProjectId !== currentProjectId;

    // Check if pipeDetails exists and matches current project ID
    const pipeDetailsMatchesProject = pipeDetails && Object.keys(pipeDetails).length > 0 &&
      Object.values(pipeDetails).some((setup: any) =>
        setup.projectId && String(setup.projectId) === String(currentProjectId)
      );

    // Only fetch pipeDetails if:
    // 1. Project ID changed (need to fetch new project's data)
    // 2. pipeDetails is null/undefined/empty
    // 3. pipeDetails exists but doesn't match current project ID
    if (currentProjectId) {
      if (projectIdChanged || !pipeDetails || Object.keys(pipeDetails).length === 0 || !pipeDetailsMatchesProject) {
        dispatch(getAllPipeDetails({ projectId: currentProjectId }));
      }

      // Fetch manufacturing categories if pipeline is enabled (this doesn't depend on project ID, so only fetch once)
      if (project.project.projectTypes.pipeline?.enabled && (!manufacturingCategories || manufacturingCategories.length === 0)) {
        dispatch(getListManufacturingCategory({}));
      }
    }

    // Update last project ID
    if (currentProjectId) {
      setLastProjectId(currentProjectId);
    }
  }, [project.project.projectId]);


  useEffect(() => {
    const fetchSettings = async () => {
      const unitFromSettings = getUnitFromSettings();
      setUnit(project.project.unit || unitFromSettings);
    };
    fetchSettings();
  }, []);

  // Refs to access child component data
  const pipelineSetupRef = useRef<PipelineSetupRef>(null);
  const fabricationSetupRef = useRef<FabricationSetupRef>(null);
  const othersSetupRef = useRef<OthersSetupRef>(null);

  const getHasAnyPipes = (state: SetupState1): boolean => {
    return state.pipeSizes && state.pipeSizes.length > 0
  }

  // Check if a pipe has valid data (not empty or zero values)
  const hasValidPipeData = (pipe: any): boolean => {
    return pipe.pipeName && pipe.pipeName.trim() !== '' &&
      pipe.wallThickness && pipe.wallThickness > 0 &&
      pipe.numberOfPipes && pipe.numberOfPipes > 0 &&
      pipe.pipeLength && pipe.pipeLength > 0 &&
      pipe.manufacturerIds && pipe.manufacturerIds.length > 0

  }

  // Check if a state has any valid pipes with actual data
  const hasValidPipesInState = (state: SetupState1): boolean => {
    if (!state.pipeSizes?.length) return false;
    if (state.projectSubType === 'spools') {
      return state.pipeSizes.some(
        (pipe) =>
          Boolean(pipe.pipeName && String(pipe.pipeName).trim() !== '') &&
          getNumberOfSpoolsFromPipe(pipe) > 0
      );
    }
    return state.pipeSizes.some((pipe: any) => hasValidPipeData(pipe));
  };

  const spoolRowsSignature = (pipe: PipeSize1, count: number): string =>
    JSON.stringify(normalizeSpoolDetailsRows(count, getSpoolDetailsFromPipe(pipe)));

  /** Detect edits to spool rows (name, type, length, welds, etc.) for fabrication spools setup. */
  const hasSpoolsSetupDetailChanges = (): boolean => {
    if (stateSpools.projectSubType !== 'spools' || !stateSpools.pipeSizes?.length) {
      return false;
    }
    const spoolsKey = String(
      stateSpools.projectTypeId ||
        project.project.projectTypes.fabrication?.options?.spools?.project_type_id ||
        ''
    );
    const originalSetup = spoolsKey ? originalSetupData[spoolsKey] : null;

    if (!originalSetup?.pipeSizes?.length) {
      return stateSpools.pipeSizes.some((pipe) => {
        const n = getNumberOfSpoolsFromPipe(pipe);
        if (n <= 0) return false;
        const rows = normalizeSpoolDetailsRows(n, getSpoolDetailsFromPipe(pipe as PipeSize1));
        return rows.some(
          (r) =>
            Boolean(r.spoolType?.trim()) ||
            Boolean(String(r.spoolLength ?? '').trim()) ||
            Boolean(String(r.welds ?? '').trim()) ||
            (Boolean(r.measureType?.trim()) && r.measureType !== 'N/A')
        );
      });
    }

    for (let i = 0; i < stateSpools.pipeSizes.length; i++) {
      const currentPipe = stateSpools.pipeSizes[i] as PipeSize1;
      const originalPipe = originalSetup.pipeSizes[i] as PipeSize1 | undefined;
      const nCur = getNumberOfSpoolsFromPipe(currentPipe);
      const nOrig = originalPipe ? getNumberOfSpoolsFromPipe(originalPipe) : 0;

      if (nCur !== nOrig) return true;

      if (
        spoolRowsSignature(currentPipe, nCur) !==
        spoolRowsSignature(originalPipe as PipeSize1, nOrig || nCur)
      ) {
        return true;
      }
    }
    return false;
  };

  // Check if there are any changes compared to the original setup data
  const hasActualChanges = (): boolean => {
    if (hasSpoolsSetupDetailChanges()) {
      return true;
    }

    const currentData = getCurrentSetupData();

    // If no current data, no changes
    if (!currentData || Object.keys(currentData).length === 0) {
      return false;
    }

    // Check if any state has valid pipes
    const hasValidPipes = hasValidPipesInState(statePipeline) ||
      hasValidPipesInState(stateBuckleArrestor) ||
      hasValidPipesInState(statePipeInPipe) ||
      hasValidPipesInState(stateBulkhead) ||
      hasValidPipesInState(stateSpools) ||
      hasValidPipesInState(stateOthers);

    if (!hasValidPipes) {
      return false;
    }

    // Compare with original setup data to detect actual changes
    for (const [key, currentSetup] of Object.entries(currentData)) {
      const originalSetup = originalSetupData[key];

      // If this is a new setup (not in original data), it's a change
      if (!originalSetup) {
        return true;
      }

      // Compare pipe sizes
      if (currentSetup.pipeSizes && originalSetup.pipeSizes) {
        // Check if number of pipes changed
        if (currentSetup.pipeSizes.length !== originalSetup.pipeSizes.length) {
          return true;
        }

        // Check if any pipe data changed
        for (let i = 0; i < currentSetup.pipeSizes.length; i++) {
          const currentPipe = currentSetup.pipeSizes[i];
          const originalPipe = originalSetup.pipeSizes[i];

          if (!originalPipe) {
            return true; // New pipe added
          }
          if (currentPipe?.manufacturerIds?.length !== originalPipe?.manufacturerIds?.length) {
            return true;
          }
          if (currentPipe?.manufacturerIds?.some((id: any) => !originalPipe?.manufacturerIds?.includes(id))) {
            return true;
          }

          // Helper function to compare jobNumber objects deeply
          const compareJobNumbers = (currentJobNumber: any, originalJobNumber: any): boolean => {
            // If both are null/undefined, they're equal
            if (!currentJobNumber && !originalJobNumber) {
              return true;
            }

            // If one is null/undefined and the other isn't, they're different
            if (!currentJobNumber || !originalJobNumber) {
              return false;
            }

            // Compare numberOfJobs
            if (currentJobNumber.numberOfJobs !== originalJobNumber.numberOfJobs) {
              return false;
            }

            // Compare jobNumbers array values (deep comparison)
            const currentJobNumbers = currentJobNumber.jobNumbers || [];
            const originalJobNumbers = originalJobNumber.jobNumbers || [];

            // Check if arrays have different lengths
            if (currentJobNumbers.length !== originalJobNumbers.length) {
              return false;
            }

            // Compare each job number string value
            for (let i = 0; i < currentJobNumbers.length; i++) {
              const currentValue = String(currentJobNumbers[i] || '').trim();
              const originalValue = String(originalJobNumbers[i] || '').trim();

              if (currentValue !== originalValue) {
                return false;
              }
            }

            return true;
          };

          //  Compare pipe properties
          if (currentPipe.pipeName !== originalPipe.pipeName ||
            currentPipe.pipeSize !== originalPipe.pipeSize ||
            currentPipe.wallThickness !== originalPipe.wallThickness ||
            currentPipe.numberOfPipes !== originalPipe.numberOfPipes ||
            currentPipe.pipeLength !== originalPipe.pipeLength ||
            !compareJobNumbers(currentPipe.jobNumber, originalPipe.jobNumber)) {
            return true;
          }

          if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'buckleArrestor') {
            if (currentPipe.numberOfBuckleArrestors !== originalPipe.numberOfBuckleArrestors ||
              currentPipe.numberOfForges !== originalPipe.numberOfForges ||
              currentPipe.outputAssemblyLength !== originalPipe.outputAssemblyLength
              || currentPipe.prefixAssemblyName !== originalPipe.prefixAssemblyName
            ) {


              return true;
            }
          }

          if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'bulkhead') {
            if (currentPipe.numberOfBuckleArrestors !== originalPipe.numberOfBuckleArrestors ||
              currentPipe.outputAssemblyLength !== originalPipe.outputAssemblyLength ||
              currentPipe.prefixAssemblyName !== originalPipe.prefixAssemblyName) {

              return true;
            }
          }

          if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'pipeInPipe') {
            if (currentPipe.numberOfBuckleArrestors !== originalPipe.numberOfBuckleArrestors ||
              currentPipe.outputAssemblyLength !== originalPipe.outputAssemblyLength ||
              currentPipe.prefixAssemblyName !== originalPipe.prefixAssemblyName) {
              return true;
            }
          }

          if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'spools') {
            if (
              getNumberOfSpoolsFromPipe(currentPipe as PipeSize1 & Record<string, unknown>) !==
                getNumberOfSpoolsFromPipe(originalPipe as PipeSize1 & Record<string, unknown>) ||
              currentPipe.numberOfForges !== originalPipe.numberOfForges ||
              currentPipe.outputAssemblyLength !== originalPipe.outputAssemblyLength ||
              currentPipe.prefixAssemblyName !== originalPipe.prefixAssemblyName
            ) {
              return true;
            }
            const curSpools = JSON.stringify(
              normalizeSpoolDetailsRows(
                getNumberOfSpoolsFromPipe(currentPipe as PipeSize1 & Record<string, unknown>),
                getSpoolDetailsFromPipe(currentPipe as PipeSize1)
              )
            );
            const origSpools = JSON.stringify(
              normalizeSpoolDetailsRows(
                getNumberOfSpoolsFromPipe(originalPipe as PipeSize1 & Record<string, unknown>),
                getSpoolDetailsFromPipe(originalPipe as PipeSize1)
              )
            );
            if (curSpools !== origSpools) {
              return true;
            }
          }

          // Compare pipeDetailsExtraDetails (e.g. isReducerPipe) for fabrication
          if (currentSetup.projectType === 'fabrication') {
            const currentReducer = currentPipe?.pipeDetailsExtraDetails && typeof currentPipe.pipeDetailsExtraDetails === 'object' && 'isReducerPipe' in currentPipe.pipeDetailsExtraDetails
              ? !!currentPipe.pipeDetailsExtraDetails.isReducerPipe
              : false;
            const originalReducer = originalPipe?.pipeDetailsExtraDetails && typeof originalPipe.pipeDetailsExtraDetails === 'object' && 'isReducerPipe' in originalPipe.pipeDetailsExtraDetails
              ? !!originalPipe.pipeDetailsExtraDetails.isReducerPipe
              : false;
            if (currentReducer !== originalReducer) {
              return true;
            }
          }

          // Compare unit/units (unit selection dropdown changes)
          const currentUnit = (currentPipe as any)?.unit || (currentPipe as any)?.units || {};
          const originalUnit = (originalPipe as any)?.unit || (originalPipe as any)?.units || {};
          const unitFields = ['pipeSize', 'wallThickness', 'length', 'outputAssemblyLength'] as const;
          for (const f of unitFields) {
            const cu = currentUnit[f] ?? defaultUnit;
            const ou = originalUnit[f] ?? defaultUnit;
            if (cu !== ou) return true;
          }
        }
      }
    }

    return false;
  }

  // Check if any state has pipes
  const hasAnyPipesInAnyState = (): boolean => {
    return getHasAnyPipes(statePipeline) ||
      getHasAnyPipes(stateBuckleArrestor) ||
      getHasAnyPipes(statePipeInPipe) ||
      getHasAnyPipes(stateBulkhead) ||
      getHasAnyPipes(stateSpools) ||
      getHasAnyPipes(stateOthers);
  }

  // Check if any state has valid pipes (with actual data)
  const hasAnyValidPipesInAnyState = (): boolean => {
    return hasValidPipesInState(statePipeline) ||
      hasValidPipesInState(stateBuckleArrestor) ||
      hasValidPipesInState(statePipeInPipe) ||
      hasValidPipesInState(stateBulkhead) ||
      hasValidPipesInState(stateSpools) ||
      hasValidPipesInState(stateOthers);
  }

  // Check if any state has numberOfPipeSizes greater than 0
  const hasAnyValidNumberOfPipeSizes = (): boolean => {
    return (Number(statePipeline.numberOfPipeSizes) > 0) ||
      (Number(stateBuckleArrestor.numberOfPipeSizes) > 0) ||
      (Number(statePipeInPipe.numberOfPipeSizes) > 0) ||
      (Number(stateBulkhead.numberOfPipeSizes) > 0) ||
      (Number(stateSpools.numberOfPipeSizes) > 0) ||
      (Number(stateOthers.numberOfPipeSizes) > 0);
  }

  // Validation function to check for empty or zero values
  const validateSetupData = (setupData: ProjectSetupData): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];



    // Check if we have any setup data at all
    const hasAnySetupData = Object.values(setupData).some((setup: any) =>
      setup && setup.pipeSizes && setup.pipeSizes.length > 0
    );


    if (!hasAnySetupData) {
      errors.push('At least one pipe size is required in any tab');
      return { isValid: false, errors };
    }

    // Validate each setup type that has data
    Object.entries(setupData).forEach(([key, currentSetup]) => {
      if (!currentSetup || !currentSetup.pipeSizes || currentSetup.pipeSizes.length === 0) {
        return; // Skip empty setups
      }

      currentSetup.pipeSizes.forEach((pipe: any, index: number) => {
        const pipeNumber = index + 1;

        // Check pipe name
        if (!pipe.pipeSize || pipe.pipeSize === 0) {
          errors.push(`Pipe ${pipeNumber}: Pipe size is required`);
        }

        // Check wall thickness
        if (!pipe.wallThickness || pipe.wallThickness === 0) {
          errors.push(`Pipe ${pipeNumber}: Wall thickness is required and cannot be zero`);
        }

        const isSpoolsFabrication =
          currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'spools';

        if (isSpoolsFabrication) {
          const spoolCount = getNumberOfSpoolsFromPipe(pipe as PipeSize1);
          if (spoolCount <= 0) {
            errors.push(`Pipe ${pipeNumber}: Number of spools is required and cannot be zero`);
          }
        } else {
          if (!pipe.numberOfPipes || pipe.numberOfPipes === 0) {
            errors.push(`Pipe ${pipeNumber}: Number of pipes is required and cannot be zero`);
          }

          if (!pipe.pipeLength || pipe.pipeLength === 0) {
            errors.push(`Pipe ${pipeNumber}: Pipe length is required and cannot be zero`);
          }
        }

        // Check manufacturer ID
        if (!pipe.manufacturerIds || pipe.manufacturerIds.length === 0) {
          errors.push(`Pipe ${pipeNumber}: Manufacturer is required`);
        }

        // Check buckle arrestors (only for buckleArrestor and bulkhead subtypes)
        if (currentSetup.projectType === 'fabrication' && (currentSetup.projectSubType === 'buckleArrestor' || currentSetup.projectSubType === 'bulkhead') && (pipe.numberOfBuckleArrestors === undefined || pipe.numberOfBuckleArrestors === null || pipe.numberOfBuckleArrestors === 0 || pipe.numberOfBuckleArrestors === '')) {
          errors.push(`Pipe ${pipeNumber}: Number of buckle arrestors is required and cannot be zero`);
        }

        // Check number of forges (only for buckleArrestor subtype)
        if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'buckleArrestor' && (pipe.numberOfForges === undefined || pipe.numberOfForges === null || pipe.numberOfForges === 0 || pipe.numberOfForges === '')) {
          errors.push(`Pipe ${pipeNumber}: Number of forges is required and cannot be zero`);
        }

        // Check output assembly length (only for buckleArrestor and bulkhead subtypes)
        if (currentSetup.projectType === 'fabrication' && (currentSetup.projectSubType === 'buckleArrestor' || currentSetup.projectSubType === 'bulkhead') && (pipe.outputAssemblyLength === undefined || pipe.outputAssemblyLength === null || pipe.outputAssemblyLength === 0 || pipe.outputAssemblyLength === '')) {
          errors.push(`Pipe ${pipeNumber}: Output assembly length is required and cannot be zero`);
        }
      });
    });

    return { isValid: errors.length === 0, errors };
  };

  // Get current setup data from all available tabs using separate state
  const getCurrentSetupData = (): ProjectSetupData => {
    const result: ProjectSetupData = {};

    // Get pipeline data from state
    if (project.project.projectTypes.pipeline?.enabled) {
      const pipelineKey = String(project.project.projectTypes.pipeline?.project_type_id);

      // Preserve existing data if available
      const existingData = setupData[pipelineKey];
      if (statePipeline.pipeSizes && statePipeline.pipeSizes.length > 0) {
        result[pipelineKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.pipeline?.project_type_id || 12,
          projectType: "pipeline",
          projectSubType: "none",
          numberOfPipeSizes: statePipeline.pipeSizes.length,
          pipeSizes: statePipeline.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              units: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              }
            };
            return convertSetupPipeToInchesForSave(pipeToSave);
          })
        };
      }
    }

    // Get fabrication data from separate states
    if (project.project.projectTypes.fabrication?.enabled) {
      // Handle buckleArrestor data
      if (stateBuckleArrestor.pipeSizes && stateBuckleArrestor.pipeSizes.length > 0) {
        const buckleArrestorKey = String(project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id);
        const existingData = setupData[buckleArrestorKey];

        result[buckleArrestorKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "buckleArrestor",
          numberOfPipeSizes: stateBuckleArrestor.pipeSizes.length,
          pipeSizes: stateBuckleArrestor.pipeSizes.length > 0 ? stateBuckleArrestor.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: pipe.pipeDetailsExtraDetails || {},
              units: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              }
            };
            return convertSetupPipeToInchesForSave(pipeToSave);
          }) : []
        };
      }

      // Handle pipeInPipe data
      if (statePipeInPipe.pipeSizes && statePipeInPipe.pipeSizes.length > 0) {
        const pipeInPipeKey = String(project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id);
        const existingData = setupData[pipeInPipeKey];

        result[pipeInPipeKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "pipeInPipe",
          numberOfPipeSizes: statePipeInPipe.pipeSizes.length,
          pipeSizes: statePipeInPipe.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfSpools ?? pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools ?? pipe.numberOfBuckleArrestors) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: pipe.pipeDetailsExtraDetails || {},
              units: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              }
            };
            return convertSetupPipeToInchesForSave(pipeToSave);
          })
        };
      }

      // Handle bulkhead data
      if (stateBulkhead.pipeSizes && stateBulkhead.pipeSizes.length > 0) {
        const bulkheadKey = String(project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id);
        const existingData = setupData[bulkheadKey];

        result[bulkheadKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "bulkhead",
          numberOfPipeSizes: stateBulkhead.pipeSizes.length,
          pipeSizes: stateBulkhead.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: pipe.pipeDetailsExtraDetails || {},
              units: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } ,
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              }
            };
            return convertSetupPipeToInchesForSave(pipeToSave);
          })
        };
      }

      // Handle spools data
      if (stateSpools.pipeSizes && stateSpools.pipeSizes.length > 0) {
        const spoolsKey = String(project.project.projectTypes.fabrication?.options?.spools?.project_type_id);
        const existingData = setupData[spoolsKey];

        result[spoolsKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.fabrication?.options?.spools?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "spools",
          numberOfPipeSizes: stateSpools.pipeSizes.length,
          pipeSizes: stateSpools.pipeSizes.map((pipe: any, index: number) => {
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );
            const spoolCount = getNumberOfSpoolsFromPipe(pipe as PipeSize1 & Record<string, unknown>);
            const spoolRows = normalizeSpoolDetailsRows(
              spoolCount,
              getSpoolDetailsFromPipe(pipe as PipeSize1)
            );
            const baseExtra =
              pipe.pipeDetailsExtraDetails && typeof pipe.pipeDetailsExtraDetails === 'object'
                ? { ...(pipe.pipeDetailsExtraDetails as Record<string, unknown>) }
                : {};
            // Spool rows live on `spoolDetails` only — do not persist them under `pipeDetailsExtraDetails`.
            delete (baseExtra as Record<string, unknown>).spoolDetails;
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: baseExtra,
              spoolDetails: buildSpoolDetailsPayload(spoolCount, spoolRows),
              units: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              }
            };
            return convertSetupPipeToInchesForSave(pipeToSave);
          })
        };
      }
    }

    // Get others data from state
    if (project.project.projectTypes.others?.enabled) {
      const othersKey = String(project.project.projectTypes.others?.project_type_id || 13);

      // Preserve existing data if available
      const existingData = setupData[othersKey];

      if (stateOthers.pipeSizes && stateOthers.pipeSizes.length > 0) {
        result[othersKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.others?.project_type_id || 13,
          projectType: "others",
          projectSubType: "none",
          numberOfPipeSizes: stateOthers.pipeSizes.length,
          pipeSizes: stateOthers.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfSpools ?? pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools ?? pipe.numberOfBuckleArrestors) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              units: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              }
            };
            return convertSetupPipeToInchesForSave(pipeToSave);
          })
        };
      }
    }

    return result;
  };

  // Function to restore setup data in ProjectSetupData format
  const restoreSetupData = (data: ProjectSetupData): void => {
    setSetupData(data);

    // The child components will automatically use the setupData prop
    // which is passed down to them, so no need to call restore methods
  };

  // Update setup data when pipeDetails changes from Redux
  useEffect(() => {
    if (pipeDetails && Object.keys(pipeDetails).length > 0) {
      setSetupData(pipeDetails);
      setOriginalSetupData(JSON.parse(JSON.stringify(pipeDetails))); // Deep copy for comparison

      // Check if we have existing data to determine edit mode
      const hasExistingData = Object.values(pipeDetails).some((setup: any) =>
        setup.pipeSizes && setup.pipeSizes.length > 0 &&
        setup.pipeSizes.some((pipe: any) => pipe.pipeId)
      );
      setEditMode(hasExistingData);
    } else if (pipeDetails && Object.keys(pipeDetails).length === 0 && dataLoaded) {
      // Only set empty data if we've already loaded (to avoid overwriting before initial load)
      setSetupData({});
      setOriginalSetupData({});
      setEditMode(false);
      // dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
    }
  }, [pipeDetails, dataLoaded]);

  // Update manufacturing list when manufacturingCategories changes from Redux
  useEffect(() => {
    if (manufacturingCategories && manufacturingCategories.length > 0) {
      // Remove duplicates and sort alphabetically by manufacturer_name
      const uniqueManufacturing = manufacturingCategories
        .filter((manufacturing: any, index: number, self: any[]) =>
          index === self.findIndex((m: any) => m.manufacturer_name === manufacturing.manufacturer_name)
        )
        .sort((a: any, b: any) => a.manufacturer_name.localeCompare(b.manufacturer_name));

      setManufacturingList(uniqueManufacturing || []);
    }
  }, [manufacturingCategories]);

  // Update loading state based on Redux
  useEffect(() => {
    setIsLoadingData(isPipeDetailsLoading || isManufacturingCategoryLoading);
    // Mark data as loaded when loading completes
    if (!isPipeDetailsLoading && !isManufacturingCategoryLoading && !dataLoaded) {
      setDataLoaded(true);
    }
  }, [isPipeDetailsLoading, isManufacturingCategoryLoading, dataLoaded]);

  const loadInitialData = async () => {
    try {
      setIsLoadingData(true);

      // Use store data if available, don't call API on initial load
      // Only fetch if pipeDetails is completely null/undefined (not just empty)
      if (pipeDetails === null || pipeDetails === undefined) {
        // Dispatch Redux action to fetch pipe details only if store is empty
        dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
      }

      // Use store data if available, only fetch if manufacturingCategories is null/undefined
      if (manufacturingCategories === null || manufacturingCategories === undefined || manufacturingCategories.length === 0) {
        // Dispatch Redux action to fetch manufacturing categories only if store is empty
        dispatch(getListManufacturingCategory({}));
      }
      // Manufacturing list will be updated via useEffect when manufacturingCategories changes
      // Reset unsaved changes after initial data load
      setHasUnsavedChanges(false);
      OnChanged(false);

    } catch (error) {
      setManufacturingList([]);
      setIsLoadingData(false);
      setDataLoaded(true);
      // Reset unsaved changes after initial data load (even on error)
      setHasUnsavedChanges(false);
      OnChanged(false);
    }
  };


  useEffect(() => {
    // Only load data once when component mounts
    if (!dataLoaded) {
      loadInitialData();
    } else {
    }
  }, [dataLoaded]);

  // Add save setup function with validation
  const handleSaveSetup = async (bool: boolean = false) => {
    try {
      setIsLoading(true);

      // Show validation when save is clicked
      setShowValidation(true);

      // Get current setup data
      const currentSetupData = getCurrentSetupData();
      // // Merge current data with existing setup data, preserving existing data for other tabs
      // const updatedSetupData: ProjectSetupData = {
      //   ...setupData,
      //   ...currentSetupData
      // };


      // Validate the setup data
      const validation = validateSetupData(currentSetupData);


      if (!validation.isValid) {
        // // Show validation errors
        customNotification.warning(
          'Validation failed',
          // `Please fix the  errors:\n${validation.errors.join('\n')}`
          'Please fill the required fields'
        );
        return;
      }
      // setSetupData(updatedSetupData);
      const res = await SaveProjectSetup(currentSetupData, false);

      // After successful save (add/update), refresh pipe details from API
      if (res && res.data) {
        dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
      }

      if (res && res.data && res.data.pipes) {
        // Helper function to compare job numbers
        const hasJobNumberChanged = (currentPipe: any, originalPipe: any | undefined): boolean => {
          // If pipe is new (no original pipe), it's a change
          if (!originalPipe) {
            return true;
          }

          // Compare job numbers
          const currentJobNumbers = currentPipe?.jobNumber?.jobNumbers || [];
          const originalJobNumbers = originalPipe?.jobNumber?.jobNumbers || [];

          // Check if arrays have different lengths
          if (currentJobNumbers.length !== originalJobNumbers.length) {
            return true;
          }

          // Check if any job number value has changed
          const normalizedCurrent = currentJobNumbers.map((j: any) => String(j || '').trim()).sort();
          const normalizedOriginal = originalJobNumbers.map((j: any) => String(j || '').trim()).sort();

          if (normalizedCurrent.length !== normalizedOriginal.length) {
            return true;
          }

          for (let i = 0; i < normalizedCurrent.length; i++) {
            if (normalizedCurrent[i] !== normalizedOriginal[i]) {
              return true;
            }
          }

          return false;
        };

        // Get all pipes from original setup data across all project types
        const getAllOriginalPipes = (): any[] => {
          const allPipes: any[] = [];
          Object.values(originalSetupData).forEach((setup: any) => {
            if (setup?.pipeSizes) {
              allPipes.push(...setup.pipeSizes);
            }
          });
          return allPipes;
        };

        const allOriginalPipes = getAllOriginalPipes();

        // Only dispatch for pipes that have changed job numbers or are new
        res.data.pipes.forEach((pipe: any) => {
          const originalPipe = allOriginalPipes.find((op: any) =>
            op.pipeId === pipe.pipeId

          );

          // Get current pipe data from currentSetupData to compare job numbers
          let currentPipe: any = pipe;

          // console.log('currentPipe', currentPipe);
          // console.log('originalPipe', originalPipe);
          // Check if job number has changed
          if (currentPipe && hasJobNumberChanged(currentPipe, originalPipe)) {
            dispatch(assignJobNumberToPipe({
              job_number: pipe.jobNumbers || currentPipe?.jobNumber?.jobNumbers || [],
              pipe_details_id: pipe.pipeId,
              project_id: project.project.projectId
            }));
          }
        });
      }

      // Update local state with the saved data
      // if(res){
      // // setSetupData(updatedSetupData);
      setEditMode(false);
      setHasUnsavedChanges(false);
      // Update original data to reflect the saved state
      setOriginalSetupData(JSON.parse(JSON.stringify(currentSetupData)));
      //  // Don't reload data to preserve current state when switching tabs
      // }
      // await loadInitialData()
      // Actions
      dispatch(fetchProjectList());
      // // Show success notification
      // customNotification.success(
      //   'Setup Saved Successfully',
      //   'The project setup has been saved successfully.'
      // );

      // Hide validation after successful save
      setShowValidation(false);

    } catch (error) {
      // Show error notification
      customNotification.error(
        'Save Failed',
        'Failed to save setup. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };
  // Function to filter setup data based on selected tab
  const getFilteredSetupData = () => {
    if (!setupData || Object.keys(setupData).length === 0) {
      return {};
    }

    const filteredData: any = {};

    if (setupTab === 'pipeline') {
      // Filter for pipeline data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'pipeline') {
          filteredData[key] = data;
        }
      });
    } else if (setupTab === 'fabrication') {
      // Filter for fabrication data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'fabrication') {
          filteredData[key] = data;
        }
      });
    } else if (setupTab === 'others') {
      // Filter for others data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'others') {
          filteredData[key] = data;
        }
      });
    }
    return filteredData;
  };



  // Separate state for each tab
  const [statePipeline, setStatePipeline] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.pipeline?.project_type_id || 0,
    projectType: "pipeline",
    projectSubType: "none",
    pipeSizes: []
  });



  // Separate state for each fabrication subtype
  const [stateBuckleArrestor, setStateBuckleArrestor] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id || 0,
    projectType: "fabrication",
    projectSubType: "buckleArrestor",
    pipeSizes: []
  });

  const [statePipeInPipe, setStatePipeInPipe] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id || 0,
    projectType: "fabrication",
    projectSubType: "pipeInPipe",
    pipeSizes: []
  });

  const [stateBulkhead, setStateBulkhead] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id || 0,
    projectType: "fabrication",
    projectSubType: "bulkhead",
    pipeSizes: []
  });

  const [stateSpools, setStateSpools] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.fabrication?.options?.spools?.project_type_id || 0,
    projectType: "fabrication",
    projectSubType: "spools",
    pipeSizes: []
  });

  const [stateOthers, setStateOthers] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.others?.project_type_id || 0,
    projectType: "others",
    projectSubType: "none",
    pipeSizes: []
  });



  // Update form state when setupData changes - handle each tab separately
  useEffect(() => {

    if (!setupData || Object.keys(setupData).length === 0) {
      return;
    }


    // Handle pipeline data
    if (project.project.projectTypes.pipeline?.enabled) {
      const pipelineData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'pipeline' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (pipelineData && pipelineData.pipeSizes) {
        setStatePipeline({
          numberOfPipeSizes: pipelineData.pipeSizes.length,
          projectId: pipelineData.projectId || 0,
          projectTypeId: pipelineData.projectTypeId || 0,
          projectType: 'pipeline',
          projectSubType: pipelineData.projectSubType || 'none',
          pipeSizes: pipelineData.pipeSizes.map((pipe: any, index: number) => {
            // Convert API values (inches) to user's saved display units
            const pipeWithUnit = { ...pipe, unit: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            const convertedPipe = convertSetupPipeFromInchesForDisplay(pipeWithUnit);
            return {
              ...convertedPipe,
              productionData: pipe.productionData || { numberOfProduction: 0, isProductionAvailable: false },
              materialsData: pipe.materialsData || { numberOfMaterials: 0, isMaterialsAvailable: false },
              documentsData: pipe.documentsData || { numberOfDocs: 0, isDocumentsAvailable: false, documents: [] }
            };
          })
        });
      }
    }



    // Handle fabrication data for each subtype
    if (project.project.projectTypes.fabrication?.enabled) {
      // Handle buckleArrestor data
      const buckleArrestorData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'fabrication' && data.projectSubType === 'buckleArrestor' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (buckleArrestorData && buckleArrestorData.pipeSizes) {
        setStateBuckleArrestor({
          numberOfPipeSizes: buckleArrestorData.pipeSizes.length,
          projectId: buckleArrestorData.projectId || 0,
          projectTypeId: buckleArrestorData.projectTypeId || 0,
          projectType: 'fabrication',
          projectSubType: 'buckleArrestor',
          pipeSizes: buckleArrestorData.pipeSizes.map((pipe: any) => {
            // Convert API values (inches) to user's saved display units
            const pipeWithUnit = { ...pipe, unit: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            return convertSetupPipeFromInchesForDisplay(pipeWithUnit);
          })
        });
      }
      else {
        setStateBuckleArrestor({
          numberOfPipeSizes: 0,
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
          projectTypeId: project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "buckleArrestor",
          pipeSizes: []
        });
      }

      // Handle pipeInPipe data
      const pipeInPipeData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'fabrication' && data.projectSubType === 'pipeInPipe' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (pipeInPipeData && pipeInPipeData.pipeSizes) {
        setStatePipeInPipe({
          numberOfPipeSizes: pipeInPipeData.pipeSizes.length,
          projectId: pipeInPipeData.projectId || 0,
          projectTypeId: pipeInPipeData.projectTypeId || 0,
          projectType: 'fabrication',
          projectSubType: 'pipeInPipe',
          pipeSizes: pipeInPipeData.pipeSizes.map((pipe: any) => {
            // Convert API values (inches) to user's saved display units
            const pipeWithUnit = { ...pipe, unit: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            return convertSetupPipeFromInchesForDisplay(pipeWithUnit);
          })
        });
      }

      // Handle bulkhead data
      const bulkheadData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'fabrication' && data.projectSubType === 'bulkhead' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (bulkheadData && bulkheadData.pipeSizes) {
        setStateBulkhead({
          numberOfPipeSizes: bulkheadData.pipeSizes.length,
          projectId: bulkheadData.projectId || 0,
          projectTypeId: bulkheadData.projectTypeId || 0,
          projectType: 'fabrication',
          projectSubType: 'bulkhead',
          pipeSizes: bulkheadData.pipeSizes.map((pipe: any) => {
            // Convert API values (inches) to user's saved display units
            const pipeWithUnit = { ...pipe, unit: pipe.units || pipe.unit || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            return convertSetupPipeFromInchesForDisplay(pipeWithUnit);
          })
        });
      }

      // Handle spools data
      const spoolsData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'fabrication' && data.projectSubType === 'spools' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (spoolsData && spoolsData.pipeSizes) {
        setStateSpools({
          numberOfPipeSizes: spoolsData.pipeSizes.length,
          projectId: spoolsData.projectId || 0,
          projectTypeId: spoolsData.projectTypeId || 0,
          projectType: 'fabrication',
          projectSubType: 'spools',
          pipeSizes: spoolsData.pipeSizes.map((pipe: any) => {
            const pipeWithUnit = { ...pipe, unit: pipe.units || pipe.unit || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            const displayed = convertSetupPipeFromInchesForDisplay(pipeWithUnit) as PipeSize1;
            const n = getNumberOfSpoolsFromPipe(displayed as PipeSize1 & Record<string, unknown>);
            return {
              ...displayed,
              spoolDetails: buildSpoolDetailsPayload(
                n,
                normalizeSpoolDetailsRows(n, getSpoolDetailsFromPipe(displayed))
              ),
            };
          })
        });
      } else {
        setStateSpools({
          numberOfPipeSizes: 0,
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
          projectTypeId: project.project.projectTypes.fabrication?.options?.spools?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "spools",
          pipeSizes: []
        });
      }
    }

    // Handle others data 
    if (project.project.projectTypes.others?.enabled) {
      const othersData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'others' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (othersData && othersData.pipeSizes) {
        setStateOthers({
          numberOfPipeSizes: othersData.pipeSizes.length,
          projectId: othersData.projectId || 0,
          projectTypeId: othersData.projectTypeId || 0,
          projectType: 'others',
          projectSubType: othersData.projectSubType || 'none',
          pipeSizes: othersData.pipeSizes.map((pipe: any) => {
            // Convert API values (inches) to user's saved display units
            const pipeWithUnit = { ...pipe, unit: pipe.units  || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            return convertSetupPipeFromInchesForDisplay(pipeWithUnit);
          })
        });
      }
    }
  }, [setupData, project.project.projectId, project.project.projectTypes.pipeline?.project_type_id, project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id, project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id, project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id, project.project.projectTypes.fabrication?.options?.spools?.project_type_id, project.project.projectTypes.others?.project_type_id]);

  useEffect(() => {

    if (!dataLoaded || isLoadingData) {
      return;
    }
    const spoolsDetailDirty = hasSpoolsSetupDetailChanges();
    const hasChanges =
      hasAnyValidNumberOfPipeSizes() &&
      (spoolsDetailDirty || (hasActualChanges() && hasAnyValidPipesInAnyState()));

    if (!hasChanges) {
      OnChanged(false);
      setHasUnsavedChanges(false);
    } else {
      OnChanged(true);
      setHasUnsavedChanges(true);
    }
  }, [
    statePipeline,
    stateBuckleArrestor,
    statePipeInPipe,
    stateBulkhead,
    stateSpools,
    stateOthers,
    originalSetupData,
    dataLoaded,
    isLoadingData,
  ]);

  // Add browser-level confirmation for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && hasActualChanges()) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // useEffect(() => {
  //   // Only check for unsaved changes after initial data has been loaded
  //   if (!dataLoaded || isLoadingData) {
  //     return;
  //   }

  //  const hasChanges = hasActualChanges() && hasAnyValidPipesInAnyState();

  //   if (hasChanges) {
  //     setHasUnsavedChanges(true);
  //   }


  // }, [statePipeline.pipeSizes, stateBuckleArrestor.pipeSizes, statePipeInPipe.pipeSizes, stateBulkhead.pipeSizes, stateOthers.pipeSizes, dataLoaded, originalSetupData]);

  return (
    <>
      {isLoadingData ? (
        <div className="flex items-center justify-center min-h-[700px]">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
            <span className="text-gray-600 text-lg">Loading setup data...</span>
          </div>
        </div>
      ) : (
        <div>
          <Card className="border-none shadow-none">
            <CardContent className="px-6 pt-2 pb-6">
              {/* Setup Category Tabs */}
              <Tabs className="w-full"
                value={setupTab}
                onValueChange={(value) => {
                  setSetupTab(value as 'pipeline' | 'fabrication' | 'others');
                }}
              >
                <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b-2 border-[#E2E5EA] flex items-end justify-between gap-4">
                  <TabsList className="h-auto justify-start gap-8 bg-transparent px-0 py-0"
                  >
                    {project.project.projectTypes.pipeline?.enabled && (
                      <TabsTrigger
                        className="relative inline-flex items-center border-b-2 border-transparent -mb-[2px] px-1 pb-3 pt-2 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-blueprint-blue transition-colors duration-150"
                        value="pipeline">
                        <Cable className="h-4 w-4 mr-2" />Pipeline
                      </TabsTrigger>
                    )}
                    {project.project.projectTypes.fabrication?.enabled && (
                      <TabsTrigger
                        className="relative inline-flex items-center border-b-2 border-transparent -mb-[2px] px-1 pb-3 pt-2 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-blueprint-blue transition-colors duration-150"
                        value="fabrication">
                        <Wrench className="h-4 w-4 mr-2" />Fabrication
                      </TabsTrigger>
                    )}
                    {project.project.projectTypes.others?.enabled && (
                      <TabsTrigger
                        className="relative inline-flex items-center border-b-2 border-transparent -mb-[2px] px-1 pb-3 pt-2 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-blueprint-blue transition-colors duration-150"
                        value="others">
                        <Activity className="h-4 w-4 mr-2" />Others
                      </TabsTrigger>
                    )}
                  </TabsList>
                  {canEdit && (
                    <Button
                      onClick={() => handleSaveSetup(true)}
                      disabled={isLoading || !hasUnsavedChanges}
                      size="sm"
                      className="bg-[#EB1C37] text-white hover:bg-[#EB1C37]/90 flex items-center gap-2 justify-center flex-shrink-0 self-center mr-2 mb-1"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Save
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {project.project.projectTypes.pipeline?.enabled && (
                  <TabsContent value="pipeline">
                    <PipelineSetup
                      ref={pipelineSetupRef}
                      project={project}
                      manufacturingList={manufacturingList}
                      setManufacturingList={setManufacturingList}
                      unit={unit}
                      setUnit={setUnit}
                      showValidation={showValidation}
                      setShowValidation={setShowValidation}
                      setupData={getFilteredSetupData()}
                      state={statePipeline}
                      setState={setStatePipeline}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      OnSave={handleSaveSetup}
                      canEdit={canEdit}
                    />
                  </TabsContent>
                )}
                {project.project.projectTypes.fabrication?.enabled && (
                  <TabsContent value="fabrication">
                    <FabricationSetup
                      ref={fabricationSetupRef}
                      project={project}
                      manufacturingList={manufacturingList}
                      setManufacturingList={setManufacturingList}
                      unit={unit}
                      setUnit={setUnit}
                      showValidation={showValidation}
                      setupData={
                        (() => {

                          return setupData
                        })()
                      }
                      buckleArrestorState={stateBuckleArrestor}
                      setBuckleArrestorState={setStateBuckleArrestor}
                      pipeInPipeState={statePipeInPipe}
                      setPipeInPipeState={setStatePipeInPipe}
                      bulkheadState={stateBulkhead}
                      setBulkheadState={setStateBulkhead}
                      spoolsState={stateSpools}
                      setSpoolsState={setStateSpools}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      canEdit={canEdit}
                    />
                  </TabsContent>
                )}

                {project.project.projectTypes.others?.enabled && (
                  <TabsContent value="others">
                    <OthersSetup
                      ref={othersSetupRef}
                      project={project}
                      manufacturingList={manufacturingList}
                      setManufacturingList={setManufacturingList}
                      unit={unit}
                      setUnit={setUnit}
                      showValidation={showValidation}
                      setShowValidation={setShowValidation}
                      setupData={getFilteredSetupData()}
                      state={stateOthers}
                      setState={setStateOthers}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      canEdit={canEdit}
                    />

                  </TabsContent>
                )}

              </Tabs>
            </CardContent>
          </Card>


        </div>
      )}
    </>
  );
}; 